import { CONTENT_SIGNAL } from "@/config/agent-content-policy";
import { isIndexableLocaleChannel, isValidLocale } from "@/config/locale";

const DISCOVERY_PATHS = new Set([
	"/robots.txt",
	"/llms.txt",
	"/sitemap.xml",
	"/feed.xml",
	"/openapi.json",
	"/api/search/index",
	"/api-docs.txt",
	"/.well-known/api-catalog",
]);

/**
 * The public discovery reference set, kept as data so the HTTP header form and the
 * HTML `<link>` form cannot drift apart. Order and spelling are the wire format.
 */
export const PUBLIC_DISCOVERY_LINKS = [
	{ href: "/.well-known/api-catalog", rel: "api-catalog", type: "application/linkset+json" },
	{ href: "/openapi.json", rel: "service-desc", type: "application/json" },
	{ href: "/api-docs.txt", rel: "service-doc", type: "text/plain" },
	{ href: "/llms.txt", rel: "describedby", type: "text/plain" },
] as const;

const DISCOVERY_LINK_VALUE = PUBLIC_DISCOVERY_LINKS.map(
	({ href, rel, type }) => `<${href}>; rel="${rel}"; type="${type}"`,
).join(", ");

const DISCOVERY_RELATIONS = new Set<string>(PUBLIC_DISCOVERY_LINKS.map(({ rel }) => rel));

const PUBLIC_CONTENT_SECTIONS = new Set([
	"products",
	"categories",
	"collections",
	"guides",
	"shipping",
	"peptides",
	"peptide-calculator",
	"half-life-calculator",
	"contact",
	"quiz",
]);

/**
 * Hard ceiling for the Link header this module writes.
 *
 * nginx answers with 502 as soon as a response header block does not fit into
 * `proxy_buffer_size`, which defaults to 4096 bytes; that is exactly how the
 * 2026-09-19 outage surfaced. A storefront response carries about 760 bytes of
 * other headers, so 2048 bytes keeps the whole block below the default buffer
 * even before our own nginx override, and still leaves more than 1 KB of room
 * for canonical and preload entries that other layers contribute. The set this
 * module writes is 246 bytes, so the ceiling is never reached by our own value —
 * it only bounds what we are willing to pass through from somewhere else.
 */
export const MAX_DISCOVERY_LINK_HEADER_BYTES = 2048;

const LINK_SEPARATOR = ", ";

/**
 * Real octets on the wire, not UTF-16 code units. Our own set is ASCII, but a foreign
 * entry may carry an RFC 8187 `title*` parameter or a percent-free IRI, and the ceiling
 * exists to protect a byte-counted proxy buffer.
 */
const linkEncoder = new TextEncoder();
function headerByteLength(value: string): number {
	return linkEncoder.encode(value).length;
}

export function isPublicDiscoveryPath(pathname: string): boolean {
	return DISCOVERY_PATHS.has(pathname);
}

/** Split a Link header at the entry separators only, never inside `<>` or a quoted string. */
function splitLinkHeader(value: string): string[] {
	const entries: string[] = [];
	let current = "";
	let inAngleBrackets = false;
	let inQuotes = false;
	for (const character of value) {
		if (character === '"' && !inAngleBrackets) inQuotes = !inQuotes;
		else if (character === "<" && !inQuotes) inAngleBrackets = true;
		else if (character === ">" && !inQuotes) inAngleBrackets = false;
		if (character === "," && !inAngleBrackets && !inQuotes) {
			entries.push(current);
			current = "";
			continue;
		}
		current += character;
	}
	entries.push(current);
	return entries.map((entry) => entry.trim()).filter(Boolean);
}

/** True for an entry that carries one of the relations this module owns. */
function isDiscoveryEntry(entry: string): boolean {
	const relation = /;\s*rel\s*=\s*"?([^";]+)"?/i.exec(entry);
	if (!relation) return false;
	return relation[1]!
		.trim()
		.split(/\s+/)
		.some((value) => DISCOVERY_RELATIONS.has(value));
}

/**
 * Write the discovery set exactly once.
 *
 * Replaces instead of appending and drops every copy of the owned relations that the
 * incoming value already carries, so running this over an already inflated header
 * collapses it back to one set rather than adding another. Foreign entries (canonical,
 * Next's preloads) keep their order and are dropped from the end only when the ceiling
 * forces it; a value that would exceed the ceiling is never written.
 */
function setPublicDiscoveryLinks(headers: Headers): void {
	if (headerByteLength(DISCOVERY_LINK_VALUE) > MAX_DISCOVERY_LINK_HEADER_BYTES) return;
	const foreign = splitLinkHeader(headers.get("Link") ?? "").filter((entry) => !isDiscoveryEntry(entry));
	const kept: string[] = [];
	let length = headerByteLength(DISCOVERY_LINK_VALUE);
	for (const entry of foreign) {
		const next = length + LINK_SEPARATOR.length + headerByteLength(entry);
		if (next > MAX_DISCOVERY_LINK_HEADER_BYTES) break;
		kept.push(entry);
		length = next;
	}
	headers.set("Link", [...kept, DISCOVERY_LINK_VALUE].join(LINK_SEPARATOR));
}

/**
 * Content-Signal for every public surface, and the discovery Link set only where the
 * response never reaches Next's route cache.
 *
 * Next captures the response headers a render touched into the route cache entry
 * (`.next/server/app/<route>.meta`) by reading them back off the live ServerResponse
 * — see `setMetadataHeader` in next/dist/server/app-render/app-render.js. Middleware
 * headers are already on that response when the render runs, so a Link value written
 * before the render ends up inside the cached entry and is appended on top of the next
 * request's fresh copy. Every revalidation then stores one copy more. That is what grew
 * the locale homepages and the product pages past nginx's header buffer on 2026-09-19.
 *
 * `routeCached: false` is for responses a route handler builds itself (the native
 * Markdown representation); the locale-less root answer is a redirect and is safe as
 * well. Rendered pages carry the same references as `<link>` elements instead.
 */
export function applyPublicDiscoveryHeaders(
	headers: Headers,
	pathname: string,
	{ routeCached = true }: { readonly routeCached?: boolean } = {},
): void {
	const segments = pathname.split("/").filter(Boolean);
	const [locale, channel, section] = segments;
	const publicStore =
		isValidLocale(locale) &&
		(channel === "eu" || channel === "us") &&
		isIndexableLocaleChannel(locale, channel);
	const homepage = pathname === "/" || (publicStore && segments.length === 2);
	if (homepage || isPublicDiscoveryPath(pathname) || (publicStore && PUBLIC_CONTENT_SECTIONS.has(section))) {
		headers.set("Content-Signal", CONTENT_SIGNAL);
	}
	if (homepage && (!routeCached || pathname === "/")) setPublicDiscoveryLinks(headers);
}
